import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense, lazy } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"
import { SelectWithOthers } from "components/SelectWithOthers"
import withRouter, { WithRouterProps } from "components/Common/withRouter"
import user1 from "assets/images/users/user-1.jpg"
import { resetProfileFlag, profileSuccess, profileError } from "store/actions"
import { updateUserApi, getUserApi, getUserProfileImageUrl, type PropertyKycBlockReason } from "helpers/auth_api"
import { useLoadScript } from "@react-google-maps/api"
import { getStatesOptions, SelectOption } from "helpers/dropdownsApi"
import {
  buildLocationPayloadFromPlace,
  enrichLocationPayloadWithRoad,
  locationPayloadToProfileFields,
  PLACES_AUTOCOMPLETE_FIELDS,
} from "helpers/googlePlacesLocation"
import KycRequiredModal from "components/KycRequiredModal/KycRequiredModal"
import "./user-profile.scss"

const KycCapture = lazy(() =>
  import("components/KycCapture/KycCapture").then((module) => ({ default: module.KycCapture }))
)

const GOOGLE_MAPS_LIBRARIES: ("places")[] = ["places"]
const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""

interface ProfileFormValues {
  fullName: string
  email: string
  mobileNumber: string
  city: string
  state: string
  address: string
  pinCode: string
  aadharNumber: string
  password: string
  confirmPassword: string
}

const profileValidationSchema = Yup.object({
  fullName: Yup.string().required("Full name is required"),
  email: Yup.string().required("Email is required").email("Invalid email"),
  mobileNumber: Yup.string().required("Mobile number is required"),
  city: Yup.string(),
  state: Yup.string(),
  address: Yup.string().required("Address is required"),
  pinCode: Yup.string()
    .matches(/^\d{6}$/, "Pin code must be 6 digits")
    .required("Pin code is required"),
  aadharNumber: Yup.string()
    .test("len", "Aadhar must be 12 digits", (val) => !val || /^\d{12}$/.test(String(val || "").replace(/\s/g, ""))),
  password: Yup.string().test(
    "minLen",
    "Password must be at least 6 characters",
    (val) => !val || val.length >= 6
  ),
  confirmPassword: Yup.string().test(
    "match",
    "Passwords must match",
    function (val) {
      const { password } = this.parent as { password?: string }
      if (!password) return true
      return val === password
    }
  ),
})

const isSessionInvalidMessage = (msg?: string) =>
  msg && /login again|another device|session expired|token expired|unauthorized/i.test(msg)

const UserProfile = (props: WithRouterProps) => {
  const { router } = props
  const { location, navigate } = router
  document.title = "Profile | SoldX"

  const dispatch = useDispatch()
  const [isEditMode, setIsEditMode] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showAadhar, setShowAadhar] = useState(false)
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null)
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null)
  const [profileImageError, setProfileImageError] = useState(false)
  const [showKycRequiredModal, setShowKycRequiredModal] = useState(false)
  const [showKycCaptureModal, setShowKycCaptureModal] = useState(false)
  const userDismissedKycRequiredAutoRef = useRef(false)
  const [loadingProfile, setLoadingProfile] = useState(true)
  const [stateOptions, setStateOptions] = useState<SelectOption[]>([])
  const addressSearchRef = useRef<HTMLInputElement>(null)
  const addressAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const formikRef = useRef<ReturnType<typeof useFormik<ProfileFormValues>>["setFieldValue"] | null>(null)
  const { isLoaded: isGoogleMapsLoaded } = useLoadScript({
    googleMapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  })
  const [userData, setUserData] = useState<{
    fullName?: string
    username?: string
    email?: string
    mobileNumber?: string
    state?: string
    city?: string
    address?: string
    pinCode?: string
    aadharNumber?: string
    role?: string
    uid?: string | number
    _id?: string
    kycComplete?: boolean
    kycSubmitted?: boolean
    kycApprovedByAdmin?: boolean
    kycSelfieDone?: boolean
    kycSignatureDone?: boolean
    profileComplete?: boolean
    profileImageUrl?: string
    accessToken?: string
    token?: string
  } | null>(null)

  const selectProfileState = (state: any) => state.Profile
  const ProfileProperties = createSelector(selectProfileState, (profile) => ({
    error: profile.error,
    success: profile.success,
  }))
  const { error, success } = useSelector(ProfileProperties)

  const loadProfile = useCallback(() => {
    const authUser = localStorage.getItem("authUser")
    if (!authUser) return
    try {
      const obj = JSON.parse(authUser)
      const uid = String(obj.uid || obj._id || "")
      const token = obj.accessToken || obj.token || ""
      if (uid && token) {
        getUserApi(uid)
          .then((res: { success: boolean; data?: Record<string, unknown> }) => {
            if (res.success && res.data) {
              const d = res.data as Record<string, unknown>
              const user = {
                ...d,
                fullName: String(d.fullName ?? d.username ?? ""),
                username: String(d.fullName ?? d.username ?? ""),
                uid: d._id ?? uid,
                _id: d._id,
                accessToken: token,
                token,
              }
              setUserData(user as any)
              localStorage.setItem("authUser", JSON.stringify(user))
            }
          })
          .catch(() => setUserData(null))
      }
    } catch {
      setUserData(null)
    }
  }, [])

  useEffect(() => {
    const authUser = localStorage.getItem("authUser")
    if (authUser) {
      try {
        const obj = JSON.parse(authUser)
        const uid = String(obj.uid || obj._id || "")
        const token = obj.accessToken || obj.token || ""
        if (uid && token) {
          getUserApi(uid)
            .then((res: { success: boolean; data?: Record<string, unknown> }) => {
              if (res.success && res.data) {
                const d = res.data as Record<string, unknown>
                const user = {
                  ...d,
                  fullName: String(d.fullName ?? d.username ?? ""),
                  username: String(d.fullName ?? d.username ?? ""),
                  uid: d._id ?? uid,
                  _id: d._id,
                  accessToken: token,
                  token,
                }
                setUserData(user as any)
                const toStore = { ...user }
                localStorage.setItem("authUser", JSON.stringify(toStore))
              }
            })
            .catch(() => setUserData(null))
            .finally(() => setLoadingProfile(false))
        } else {
          setUserData(null)
          setLoadingProfile(false)
        }
      } catch {
        setUserData(null)
        setLoadingProfile(false)
      }
    } else {
      setUserData(null)
      setLoadingProfile(false)
    }
  }, [dispatch])

  useEffect(() => {
    if (!success) return
    const timer = setTimeout(() => dispatch(resetProfileFlag()), 3000)
    return () => clearTimeout(timer)
  }, [success, dispatch])

  useEffect(() => {
    if (!error) return
    const timer = setTimeout(() => dispatch(resetProfileFlag()), 5000)
    return () => clearTimeout(timer)
  }, [error, dispatch])

  /** Same as “KYC not submitted” badges: profile done, upload still needed (show KycRequiredModal by default). */
  const kycSubmitted = useMemo(() => {
    if (!userData) return false
    return (
      userData.kycSubmitted === true ||
      !!(userData.kycSelfieDone && userData.kycSignatureDone)
    )
  }, [userData])

  const kycRequiresRequiredModal = useMemo(() => {
    if (loadingProfile || !userData) return false
    const profileComplete =
      userData.profileComplete ??
      !!(userData.address?.trim() && userData.pinCode?.trim() && userData.mobileNumber?.trim() && userData.aadharNumber?.trim())
    if (!profileComplete || userData.kycComplete || kycSubmitted) return false
    return true
  }, [loadingProfile, userData, kycSubmitted])

  const kycModalVariant: PropertyKycBlockReason = kycSubmitted ? "pending" : "upload"

  useEffect(() => {
    if (kycSubmitted) {
      setShowKycRequiredModal(false)
    }
  }, [kycSubmitted])

  useEffect(() => {
    if (showKycCaptureModal) return
    if (!kycRequiresRequiredModal) {
      userDismissedKycRequiredAutoRef.current = false
      return
    }
    if (userDismissedKycRequiredAutoRef.current) return
    setShowKycRequiredModal(true)
  }, [kycRequiresRequiredModal, showKycCaptureModal])

  /** From home/properties: KycRequiredModal links to /profile?kycCapture=1 → camera modal directly (skip info modal). */
  useEffect(() => {
    if (loadingProfile) return
    const params = new URLSearchParams(location.search)
    if (params.get("kycCapture") !== "1") return
    setShowKycRequiredModal(false)
    userDismissedKycRequiredAutoRef.current = true
    setShowKycCaptureModal(true)
    navigate("/profile", { replace: true })
  }, [loadingProfile, location.search, navigate])

  const handleCloseKycRequiredModal = useCallback(() => {
    setShowKycRequiredModal(false)
    userDismissedKycRequiredAutoRef.current = true
  }, [])

  const handleCloseKycCaptureModal = useCallback(() => {
    setShowKycCaptureModal(false)
  }, [])

  /** Close info modal (if open) and open camera / KYC capture */
  const openKycCaptureFlow = useCallback(() => {
    setShowKycRequiredModal(false)
    setShowKycCaptureModal(true)
  }, [])

  const handleEditClick = useCallback(() => {
    getStatesOptions()
      .then(setStateOptions)
      .catch(() => setStateOptions([]))
      .finally(() => setIsEditMode(true))
  }, [])

  const editForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: userData?.fullName || userData?.username || "",
      email: userData?.email || "",
      mobileNumber: userData?.mobileNumber || "",
      city: userData?.city || "",
      state: userData?.state || "",
      address: userData?.address || "",
      pinCode: userData?.pinCode || "",
      aadharNumber: userData?.aadharNumber || "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: profileValidationSchema,
    onSubmit: async (values: ProfileFormValues) => {
      setIsSaving(true)
      const uid = String(userData?.uid ?? userData?._id ?? "")
      if (!uid) {
        dispatch(profileError("User ID not found"))
        setIsSaving(false)
        return
      }
      try {
        const res = await updateUserApi({
          id: uid,
          fullName: values.fullName,
          email: values.email,
          mobileNumber: values.mobileNumber,
          city: values.city,
          state: values.state,
          address: values.address,
          pinCode: values.pinCode?.trim() || undefined,
          role: userData?.role,
          aadharNumber: values.aadharNumber?.trim() || undefined,
          ...(values.password && { password: values.password }),
          ...(profileImageFile && { profileImage: profileImageFile }),
        })
        if (res.success) {
          const updatedUser = {
            ...userData,
            ...values,
            fullName: values.fullName,
            username: values.fullName,
            ...(res.data && res.data),
            accessToken: userData?.accessToken,
            token: userData?.token,
          }
          delete (updatedUser as any).password
          delete (updatedUser as any).confirmPassword
          localStorage.setItem("authUser", JSON.stringify(updatedUser))
          setUserData(updatedUser as any)
          window.dispatchEvent(new CustomEvent("profileUpdated"))
          setIsEditMode(false)
          setProfileImageFile(null)
          setProfileImagePreview(null)
          editForm.setFieldValue("password", "")
          editForm.setFieldValue("confirmPassword", "")
          dispatch(profileSuccess("Profile updated successfully"))
        } else {
          const msg = res.message || "Update failed"
          dispatch(profileError(msg))
          if (isSessionInvalidMessage(msg)) {
            localStorage.removeItem("authUser")
            setTimeout(() => router.navigate("/login"), 1500)
          }
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || ""
        if (err?.response?.status === 401 || isSessionInvalidMessage(msg)) {
          dispatch(profileError(msg || "Session expired. Please login again."))
          localStorage.removeItem("authUser")
          setTimeout(() => router.navigate("/login"), 1500)
        } else if (err?.response) {
          // API responded with error (4xx/5xx) - show error, no success
          dispatch(profileError(msg || "Update failed"))
        } else {
          // Network error / API not available - fallback to localStorage only
          const updatedUser = { ...userData, ...values, fullName: values.fullName, username: values.fullName }
          delete (updatedUser as any).password
          delete (updatedUser as any).confirmPassword
          localStorage.setItem("authUser", JSON.stringify(updatedUser))
          setUserData(updatedUser as any)
          setIsEditMode(false)
          setProfileImageFile(null)
          setProfileImagePreview(null)
          window.dispatchEvent(new CustomEvent("profileUpdated"))
          dispatch(profileSuccess("Profile updated (saved locally)"))
        }
      } finally {
        setIsSaving(false)
      }
    },
  })

  formikRef.current = editForm.setFieldValue

  const handleCancelEdit = useCallback(() => {
    setIsEditMode(false)
    setProfileImageFile(null)
    setProfileImagePreview(null)
    editForm.setFieldValue("password", "")
    editForm.setFieldValue("confirmPassword", "")
  }, [editForm])

  const displayName = userData?.fullName || userData?.username || "User"

  // Reset image error when profile or preview changes so we retry loading the real image
  useEffect(() => {
    setProfileImageError(false)
  }, [userData?.profileImageUrl, profileImagePreview])

  // Address autocomplete — same logic as Create List Property
  useEffect(() => {
    if (!isEditMode || !isGoogleMapsLoaded || !googleMapsApiKey || !window.google?.maps?.places) return
    const t = setTimeout(() => {
      const el = addressSearchRef.current
      if (!el) return
      if (addressAutocompleteRef.current && window.google?.maps?.event?.clearInstanceListeners) {
        window.google.maps.event.clearInstanceListeners(addressAutocompleteRef.current)
        addressAutocompleteRef.current = null
      }
      const autocomplete = new window.google.maps.places.Autocomplete(el, {
        componentRestrictions: { country: "in" },
        fields: [...PLACES_AUTOCOMPLETE_FIELDS],
        types: [],
      })
      addressAutocompleteRef.current = autocomplete
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace()
        if (!place.formatted_address && !place.geometry?.location) return
        const setFieldValue = formikRef.current
        if (!setFieldValue) return
        const basePayload = buildLocationPayloadFromPlace(place, stateOptions)
        enrichLocationPayloadWithRoad(basePayload, place).then((payload) => {
          const fields = locationPayloadToProfileFields(payload)
          setFieldValue("address", fields.address)
          setFieldValue("city", fields.city)
          setFieldValue("state", fields.state)
          setFieldValue("pinCode", fields.pinCode)
          if (addressSearchRef.current && fields.address) {
            addressSearchRef.current.value = fields.address
          }
        })
      })
    }, 150)
    return () => {
      clearTimeout(t)
      if (addressAutocompleteRef.current && window.google?.maps?.event?.clearInstanceListeners) {
        window.google.maps.event.clearInstanceListeners(addressAutocompleteRef.current)
        addressAutocompleteRef.current = null
      }
    }
  }, [isEditMode, isGoogleMapsLoaded, stateOptions])

  // Sync address input when profile loads in edit mode
  useEffect(() => {
    if (!isEditMode) return
    if (addressSearchRef.current) {
      addressSearchRef.current.value = editForm.values.address || ""
    }
  }, [isEditMode, editForm.values.address])

  if (loadingProfile) {
    return (
      <div className="page-content" style={{ minHeight: "60vh", background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)" }}>
        <Container fluid className="py-5">
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" style={{ width: "3rem", height: "3rem" }} role="status" />
            <p className="text-muted mb-0">Loading your profile...</p>
          </div>
        </Container>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="">
        <Container fluid className="py-4">
          {/* Profile Header Card - enhanced */}
          <Card className="profile-header-card overflow-hidden border-0 mb-4" style={{ borderRadius: "20px", boxShadow: "0 20px 60px rgba(0,0,0,0.08), 0 4px 12px rgba(37,99,235,0.06)" }}>
            <div
              className="profile-header-bg position-relative"
              style={{
                height: "140px",
                background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 35%, #2563eb 70%, #3b82f6 100%)",
                backgroundSize: "200% 200%",
              }}
            >
              {/* <div className="position-absolute w-100 h-100" style={{ opacity: 0.12, background: "url('https://images.unsplash.com/photo-1557683316-973673baf926?w=800') center/cover" }} /> */}
              <div className="position-absolute w-100 h-100" style={{ background: "radial-gradient(ellipse 80% 50% at 50% -20%, rgba(255,255,255,0.25), transparent)" }} />
            </div>
            <CardBody className="pt-2 pb-4">
              <Row className="align-items-center">
                <Col>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-4">
                    <div className="d-flex align-items-center gap-4">
                      <div className="profile-avatar-wrap position-relative" style={{ marginTop: "-110px" }}>
                        <div
                          className="rounded-circle border-white d-flex align-items-center justify-content-center overflow-hidden"
                          style={{
                            width: "140px",
                            height: "140px",
                            border: "4px solid #fff",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.15), 0 2px 8px rgba(37,99,235,0.2)",
                            background: "#f1f5f9",
                          }}
                        >
                          <img
                            key={profileImageError ? "fallback" : (profileImagePreview || getUserProfileImageUrl(userData) || "default")}
                            src={profileImageError ? user1 : (profileImagePreview || getUserProfileImageUrl(userData) || user1)}
                            alt={displayName}
                            className="rounded-circle"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            referrerPolicy="no-referrer"
                            onError={() => {
                              setProfileImageError(true)
                            }}
                          />
                        </div>
                        {isEditMode && (
                          <label
                            className="position-absolute bottom-0 end-0 rounded-circle text-white d-flex align-items-center justify-content-center shadow"
                            style={{ width: "36px", height: "36px", cursor: "pointer", background: "linear-gradient(135deg, #2563eb, #3b82f6)" }}
                          >
                            <i className="mdi mdi-camera" style={{ fontSize: "18px" }} />
                            <input
                              type="file"
                              accept="image/*"
                              className="d-none"
                              onChange={(e) => {
                                const f = e.target.files?.[0]
                                if (f) {
                                  setProfileImageFile(f)
                                  setProfileImagePreview(URL.createObjectURL(f))
                                }
                              }}
                            />
                          </label>
                        )}
                        {userData?.kycComplete && (
                          <span
                            className="position-absolute bottom-0 end-0 rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: "28px", height: "28px", background: "linear-gradient(135deg, #059669, #10b981)", boxShadow: "0 2px 8px rgba(5,150,105,0.4)" }}
                            title="KYC Verified"
                          >
                            <i className="mdi mdi-check text-white" style={{ fontSize: "16px" }} />
                          </span>
                        )}
                      </div>
                      <div className="pb-1">
                        <h4 className="mb-1 fw-bold" style={{ fontSize: "1.5rem", color: "#0f172a", letterSpacing: "-0.02em" }}>
                          {displayName}
                        </h4>
                        <p className="text-muted mb-2" style={{ fontSize: "0.95rem" }}>
                          <i className="mdi mdi-email-outline me-1" />
                          {userData?.email}
                        </p>
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                          {userData?.role && (
                            <Badge
                              className="px-3 py-2 text-capitalize"
                              style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", border: "none", fontWeight: 600, fontSize: "0.8rem" }}
                            >
                              {userData.role}
                            </Badge>
                          )}
                          {(() => {
                            const profileComplete = userData?.profileComplete ?? !!(userData?.address?.trim() && userData?.pinCode?.trim() && userData?.mobileNumber?.trim() && userData?.aadharNumber?.trim())
                            if (!profileComplete) {
                              return (
                                <span className="text-muted small">
                                  <i className="mdi mdi-information-outline me-1" />
                                  Complete your profile (Address, Mobile number, Aadhar) to proceed with KYC.
                                </span>
                              )
                            }
                            if (userData?.kycComplete) {
                              return (
                                <Badge className="px-3 py-2" style={{ background: "linear-gradient(135deg, #059669, #10b981)", border: "none", fontWeight: 600 }}>
                                  <i className="mdi mdi-shield-check me-1" /> KYC Verified
                                </Badge>
                              )
                            }
                            const kycSubmitted =
                              userData?.kycSubmitted === true ||
                              !!(userData?.kycSelfieDone && userData?.kycSignatureDone)
                            if (kycSubmitted) {
                              return (
                                <Badge className="px-3 py-2" style={{ background: "linear-gradient(135deg, #ca8a04, #eab308)", border: "none", fontWeight: 600 }}>
                                  <i className="mdi mdi-clock-outline me-1" /> Awaiting admin approval
                                </Badge>
                              )
                            }
                            return (
                              <>
                                <Badge className="px-3 py-2" style={{ background: "linear-gradient(135deg, #d97706, #f59e0b)", border: "none", fontWeight: 600 }}>
                                  <i className="mdi mdi-shield-alert me-1" /> KYC not submitted
                                </Badge>
                                <Button
                                  size="sm"
                                  className="px-3 py-1"
                                  style={{ background: "linear-gradient(135deg, #d97706, #f59e0b)", border: "none", fontWeight: 600 }}
                                  onClick={openKycCaptureFlow}
                                >
                                  <i className="mdi mdi-shield-check me-1" />
                                  Complete your KYC
                                </Button>
                              </>
                            )
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {error && (
            <Alert color="danger" className="mb-4 d-flex align-items-center border-0 shadow-sm" style={{ borderRadius: "12px" }}>
              <i className="mdi mdi-alert-circle text-danger me-2" style={{ fontSize: "1.25rem" }} />
              <span>{error}</span>
            </Alert>
          )}
          {success && (
            <Alert color="success" className="mb-4 d-flex align-items-center border-0 shadow-sm" style={{ borderRadius: "12px" }}>
              <i className="mdi mdi-check-circle text-success me-2" style={{ fontSize: "1.25rem" }} />
              <span>{success}</span>
            </Alert>
          )}

          <Row>
            {/* Profile Details */}
            <Col lg={8}>
              <Card className="border-0 mb-4 overflow-hidden" style={{ borderRadius: "16px", boxShadow: "0 10px 40px rgba(0,0,0,0.06)" }}>
                <div style={{ height: "4px", background: "linear-gradient(90deg, #2563eb, #6366f1)" }} />
                <CardBody className="pt-4">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
                    <h5 className="card-title mb-0 d-flex align-items-center" style={{ color: "#0f172a", fontWeight: 700 }}>
                      <span className="d-inline-flex align-items-center justify-content-center rounded-3 me-2" style={{ width: "40px", height: "40px", background: "linear-gradient(135deg, #2563eb20, #6366f120)" }}>
                        <i className="mdi mdi-account-circle text-primary" style={{ fontSize: "1.35rem" }} />
                      </span>
                      Profile Information
                    </h5>
                    {!isEditMode ? (
                      <Button
                        color="primary"
                        onClick={handleEditClick}
                        className="px-4 py-2 fw-semibold"
                        style={{ borderRadius: "10px", boxShadow: "0 4px 14px rgba(37,99,235,0.35)" }}
                      >
                        <i className="mdi mdi-pencil me-1" />
                        Edit Profile
                      </Button>
                    ) : (
                      <button
                        type="button"
                        className="profile-cancel-btn px-4 py-2 fw-semibold rounded-3"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                  {isEditMode ? (
                    <Form onSubmit={editForm.handleSubmit}>
                      <Row className="g-4">
                        <Col md={6}>
                          <Label for="fullName" className="fw-semibold text-dark">Full Name *</Label>
                          <Input
                            id="fullName"
                            name="fullName"
                            className="form-control"
                            style={{ height: "42px", borderRadius: "10px", border: "1px solid #e2e8f0", boxSizing: "border-box" }}
                            placeholder="Enter full name"
                            value={editForm.values.fullName}
                            onChange={editForm.handleChange}
                            onBlur={editForm.handleBlur}
                            invalid={!!(editForm.touched.fullName && editForm.errors.fullName)}
                          />
                          {editForm.touched.fullName && editForm.errors.fullName && (
                            <FormFeedback type="invalid">{editForm.errors.fullName}</FormFeedback>
                          )}
                        </Col>
                        <Col md={6}>
                          <Label for="email" className="fw-semibold text-dark">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            className="form-control"
                            style={{ height: "42px", borderRadius: "10px", border: "1px solid #e2e8f0", boxSizing: "border-box" }}
                            placeholder="Enter email"
                            value={editForm.values.email}
                            onChange={editForm.handleChange}
                            onBlur={editForm.handleBlur}
                            invalid={!!(editForm.touched.email && editForm.errors.email)}
                          />
                          {editForm.touched.email && editForm.errors.email && (
                            <FormFeedback type="invalid">{editForm.errors.email}</FormFeedback>
                          )}
                        </Col>
                        <Col md={6}>
                          <Label for="mobileNumber" className="fw-semibold text-dark">Mobile Number *</Label>
                          <Input
                            id="mobileNumber"
                            name="mobileNumber"
                            type="tel"
                            className="form-control"
                            style={{ height: "42px", borderRadius: "10px", border: "1px solid #e2e8f0", boxSizing: "border-box" }}
                            placeholder="Enter mobile number"
                            value={editForm.values.mobileNumber}
                            onChange={editForm.handleChange}
                            onBlur={editForm.handleBlur}
                            invalid={!!(editForm.touched.mobileNumber && editForm.errors.mobileNumber)}
                          />
                          {editForm.touched.mobileNumber && editForm.errors.mobileNumber && (
                            <FormFeedback type="invalid">{editForm.errors.mobileNumber}</FormFeedback>
                          )}
                        </Col>

                        <Col md={6}>
                          <Label for="aadharNumber" className="fw-semibold text-dark">Aadhar Number *</Label>
                          <Input
                            id="aadharNumber"
                            name="aadharNumber"
                            type="text"
                            inputMode="numeric"
                            maxLength={14}
                            className="form-control"
                            style={{ height: "42px", borderRadius: "10px", border: "1px solid #e2e8f0", boxSizing: "border-box" }}
                            placeholder="12 digits"
                            value={editForm.values.aadharNumber}
                            onChange={(e) => {
                              const v = e.target.value.replace(/\D/g, "").slice(0, 12)
                              editForm.setFieldValue("aadharNumber", v)
                            }}
                            onBlur={editForm.handleBlur}
                            invalid={!!(editForm.touched.aadharNumber && editForm.errors.aadharNumber)}
                          />
                          {editForm.touched.aadharNumber && editForm.errors.aadharNumber && (
                            <FormFeedback type="invalid">{editForm.errors.aadharNumber}</FormFeedback>
                          )}
                        </Col>
                        <Col xs={12}>
                          <Label for="addressSearch" className="fw-semibold text-dark">Address *</Label>
                          <input
                            ref={addressSearchRef}
                            id="addressSearch"
                            name="address"
                            type="text"
                            className={`form-control${editForm.touched.address && editForm.errors.address ? " is-invalid" : ""}`}
                            style={{ height: "42px", borderRadius: "10px", border: "1px solid #e2e8f0", boxSizing: "border-box" }}
                            defaultValue={editForm.values.address || ""}
                            onChange={(e) => editForm.setFieldValue("address", e.target.value)}
                            onBlur={(e) => {
                              editForm.setFieldValue("address", e.target.value)
                              editForm.setFieldTouched("address", true)
                            }}
                            placeholder="Type to search address or building name, or enter manually"
                            autoComplete="off"
                            disabled={!isGoogleMapsLoaded && !!googleMapsApiKey}
                            aria-label="Address"
                          />
                          {editForm.touched.address && editForm.errors.address && (
                            <FormFeedback type="invalid">{editForm.errors.address}</FormFeedback>
                          )}
                          {!googleMapsApiKey ? (
                            <p className="small text-muted mt-1 mb-0">Set REACT_APP_GOOGLE_MAPS_API_KEY in .env to enable address search.</p>
                          ) : (
                            <p className="small text-muted mt-1 mb-0">Start typing to search — select a suggestion to auto-fill city, state and pin code. You can edit the address anytime.</p>
                          )}
                        </Col>
                        <Col md={6}>
                          <Label for="city" className="fw-semibold text-dark">City</Label>
                          <Input
                            id="city"
                            name="city"
                            className="form-control"
                            style={{ height: "42px", borderRadius: "10px", border: "1px solid #e2e8f0", boxSizing: "border-box" }}
                            placeholder="Enter city"
                            value={editForm.values.city}
                            onChange={editForm.handleChange}
                          />
                        </Col>
                        <Col md={6}>
                          <Label for="state" className="fw-semibold text-dark">State</Label>
                          <SelectWithOthers
                            placeholder="Select state"
                            value={editForm.values.state || ""}
                            onChange={(v) => editForm.setFieldValue("state", v)}
                            options={stateOptions}
                            classNamePrefix="select2-selection"
                            isClearable
                            otherPlaceholder="Enter state / UT"
                            styles={{
                              control: (base) => ({ ...base, height: "42px", minHeight: "42px", borderRadius: "10px", border: "1px solid #e2e8f0", boxSizing: "border-box" }),
                              dropdownIndicator: (base) => ({ ...base }),
                              menu: (base) => ({ ...base, borderRadius: "10px" }),
                            }}
                          />
                        </Col>
                        <Col md={6}>
                          <Label for="pinCode" className="fw-semibold text-dark">Pin Code</Label>
                          <Input
                            id="pinCode"
                            name="pinCode"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            className="form-control"
                            style={{ height: "42px", borderRadius: "10px", border: "1px solid #e2e8f0", boxSizing: "border-box" }}
                            placeholder="6-digit postal code"
                            value={editForm.values.pinCode}
                            onChange={(e) => {
                              const v = e.target.value.replace(/\D/g, "").slice(0, 6)
                              editForm.setFieldValue("pinCode", v)
                            }}
                            onBlur={editForm.handleBlur}
                            invalid={!!(editForm.touched.pinCode && editForm.errors.pinCode)}
                          />
                          {editForm.touched.pinCode && editForm.errors.pinCode && (
                            <FormFeedback type="invalid">{editForm.errors.pinCode}</FormFeedback>
                          )}
                        </Col>
                        <Col md={6}>
                          <Label for="password" className="fw-semibold text-dark">New Password</Label>
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            className="form-control"
                            style={{ height: "42px", borderRadius: "10px", border: "1px solid #e2e8f0", boxSizing: "border-box" }}
                            placeholder="Leave blank to keep current"
                            value={editForm.values.password}
                            onChange={editForm.handleChange}
                            onBlur={editForm.handleBlur}
                            invalid={!!(editForm.touched.password && editForm.errors.password)}
                          />
                          {editForm.touched.password && editForm.errors.password && (
                            <FormFeedback type="invalid">{editForm.errors.password}</FormFeedback>
                          )}
                        </Col>
                        <Col md={6}>
                          <Label for="confirmPassword" className="fw-semibold text-dark">Confirm Password</Label>
                          <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            className="form-control"
                            style={{ height: "42px", borderRadius: "10px", border: "1px solid #e2e8f0", boxSizing: "border-box" }}
                            placeholder="Re-enter new password"
                            value={editForm.values.confirmPassword}
                            onChange={editForm.handleChange}
                            onBlur={editForm.handleBlur}
                            invalid={!!(editForm.touched.confirmPassword && editForm.errors.confirmPassword)}
                          />
                          {editForm.touched.confirmPassword && editForm.errors.confirmPassword && (
                            <FormFeedback type="invalid">{editForm.errors.confirmPassword}</FormFeedback>
                          )}
                        </Col>
                        <Col xs={12} className="pt-2">
                          <Button
                            type="submit"
                            color="primary"
                            disabled={isSaving}
                            className="px-4 py-2 fw-semibold"
                          >
                            {isSaving ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="mdi mdi-content-save me-2" />
                                Save Changes
                              </>
                            )}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  ) : (
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="p-3 rounded-3" style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}>
                          <Label className="text-muted small text-uppercase mb-1 d-flex align-items-center gap-1">
                            <i className="mdi mdi-account-outline" style={{ fontSize: "0.9rem" }} /> Full Name
                          </Label>
                          <p className="mb-0 fw-semibold" style={{ color: "#0f172a" }}>{displayName}</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="p-3 rounded-3" style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}>
                          <Label className="text-muted small text-uppercase mb-1 d-flex align-items-center gap-1">
                            <i className="mdi mdi-email-outline" style={{ fontSize: "0.9rem" }} /> Email
                          </Label>
                          <p className="mb-0 fw-semibold" style={{ color: "#0f172a" }}>{userData?.email || "—"}</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="p-3 rounded-3" style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}>
                          <Label className="text-muted small text-uppercase mb-1 d-flex align-items-center gap-1">
                            <i className="mdi mdi-phone-outline" style={{ fontSize: "0.9rem" }} /> Mobile Number
                          </Label>
                          <p className="mb-0 fw-semibold" style={{ color: "#0f172a" }}>{userData?.mobileNumber || "—"}</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="p-3 rounded-3" style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}>
                          <Label className="text-muted small text-uppercase mb-1 d-flex align-items-center gap-1">
                            <i className="mdi mdi-city" style={{ fontSize: "0.9rem" }} /> City
                          </Label>
                          <p className="mb-0 fw-semibold" style={{ color: "#0f172a" }}>{userData?.city || "—"}</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="p-3 rounded-3" style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}>
                          <Label className="text-muted small text-uppercase mb-1 d-flex align-items-center gap-1">
                            <i className="mdi mdi-map-marker" style={{ fontSize: "0.9rem" }} /> State
                          </Label>
                          <p className="mb-0 fw-semibold" style={{ color: "#0f172a" }}>{userData?.state || "—"}</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="p-3 rounded-3" style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}>
                          <Label className="text-muted small text-uppercase mb-1 d-flex align-items-center gap-1">
                            <i className="mdi mdi-card-account-details-outline" style={{ fontSize: "0.9rem" }} /> Aadhar Number
                          </Label>
                          <div className="d-flex align-items-center gap-2">
                            <p className="mb-0 fw-semibold" style={{ color: "#0f172a" }}>
                              {userData?.aadharNumber
                                ? showAadhar
                                  ? String(userData.aadharNumber).replace(/(\d{4})(?=\d)/g, "$1 ")
                                  : "•••• •••• •••• " + String(userData.aadharNumber).slice(-4)
                                : "—"}
                            </p>
                            {userData?.aadharNumber && (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-muted"
                                onClick={() => setShowAadhar((v) => !v)}
                                title={showAadhar ? "Hide Aadhar" : "Show Aadhar"}
                                aria-label={showAadhar ? "Hide Aadhar" : "Show Aadhar"}
                              >
                                <i className={`mdi mdi-${showAadhar ? "eye-off" : "eye"}`} style={{ fontSize: "1.2rem" }} />
                              </button>
                            )}
                          </div>
                        </div>
                      </Col>
                      <Col xs={12}>
                        <div className="p-3 rounded-3" style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}>
                          <Label className="text-muted small text-uppercase mb-1 d-flex align-items-center gap-1">
                            <i className="mdi mdi-home-map-marker" style={{ fontSize: "0.9rem" }} /> Address
                          </Label>
                          <p className="mb-0 fw-semibold" style={{ color: "#0f172a" }}>{userData?.address || "—"}</p>
                          {userData?.pinCode && (
                            <p className="mb-0 small mt-1" style={{ color: "#64748b" }}>Pin code: {userData.pinCode}</p>
                          )}
                        </div>
                      </Col>
                      {/* <Col xs={12}>
                        <div className="p-3 rounded-3" style={{ background: "#f1f5f9", border: "1px solid #e2e8f0" }}>
                          <Label className="text-muted small text-uppercase mb-1 d-flex align-items-center gap-1">
                            <i className="mdi mdi-identifier" style={{ fontSize: "0.9rem" }} /> User ID
                          </Label>
                          <p className="mb-0 font-monospace small" style={{ color: "#64748b" }}>{userData?.uid || "—"}</p>
                        </div>
                      </Col> */}
                    </Row>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Side Info */}
            <Col lg={4}>
              <Card className="border-0 mb-4 overflow-hidden" style={{ borderRadius: "16px", boxShadow: "0 10px 40px rgba(0,0,0,0.06)" }}>
                <div style={{ height: "4px", background: "linear-gradient(90deg, #6366f1, #8b5cf6)" }} />
                <CardBody>
                  <h6 className="text-uppercase fw-bold mb-4" style={{ color: "#64748b", fontSize: "0.75rem", letterSpacing: "0.08em" }}>
                    Quick Stats
                  </h6>
                  {(() => {
                    const profileComplete = userData?.profileComplete ?? !!(userData?.address?.trim() && userData?.pinCode?.trim() && userData?.mobileNumber?.trim() && userData?.aadharNumber?.trim())
                    const kycComplete = !!userData?.kycComplete
                    const kycSubmitted =
                      userData?.kycSubmitted === true ||
                      !!(userData?.kycSelfieDone && userData?.kycSignatureDone)
                    const awaitingAdmin = profileComplete && kycSubmitted && !kycComplete
                    const needKycUpload = profileComplete && !kycSubmitted
                    const cardStyle = kycComplete
                      ? { background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", border: "1px solid #a7f3d0" }
                      : awaitingAdmin
                        ? { background: "linear-gradient(135deg, #fef9c3 0%, #fef08a 100%)", border: "1px solid #facc15" }
                        : needKycUpload
                          ? { background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)", border: "1px solid #fcd34d" }
                          : { background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", border: "1px solid #e2e8f0" }
                    const iconStyle = kycComplete
                      ? { background: "linear-gradient(135deg, #059669, #10b981)" }
                      : awaitingAdmin
                        ? { background: "linear-gradient(135deg, #ca8a04, #eab308)" }
                        : needKycUpload
                          ? { background: "linear-gradient(135deg, #d97706, #f59e0b)" }
                          : { background: "linear-gradient(135deg, #64748b, #94a3b8)" }
                    const textColor = kycComplete ? "#047857" : awaitingAdmin ? "#a16207" : needKycUpload ? "#b45309" : "#64748b"
                    const statusLine = kycComplete
                      ? "Verified — you can bid, view properties, and use listings."
                      : awaitingAdmin
                        ? "Documents submitted — waiting for administrator approval."
                        : needKycUpload
                          ? "Submit selfie and signature from Complete KYC."
                          : "Complete profile (Address, Pin code, Mobile, Aadhar) first."
                    return (
                      <div className="d-flex align-items-center mb-3 p-3 rounded-3" style={cardStyle}>
                        <div className="flex-shrink-0 me-3">
                          <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px", ...iconStyle }}>
                            <i className={`mdi ${awaitingAdmin ? "mdi-clock-outline" : "mdi-shield-check"} text-white`} style={{ fontSize: "1.5rem" }} />
                          </div>
                        </div>
                        <div>
                          <p className="mb-0 fw-bold" style={{ color: "#0f172a" }}>KYC Status</p>
                          <p className="mb-0 small" style={{ color: textColor }}>
                            {statusLine}
                          </p>
                        </div>
                      </div>
                    )
                  })()}
                  <div className="d-flex align-items-center p-3 rounded-3" style={{ background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)", border: "1px solid #c7d2fe" }}>
                    <div className="flex-shrink-0 me-3">
                      <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px", background: "linear-gradient(135deg, #6366f1, #8b5cf6)" }}>
                        <i className="mdi mdi-account text-white" style={{ fontSize: "1.5rem" }} />
                      </div>
                    </div>
                    <div>
                      <p className="mb-0 fw-bold" style={{ color: "#0f172a" }}>Account Type</p>
                      <p className="mb-0 small text-capitalize" style={{ color: "#4f46e5" }}>{userData?.role || "User"}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card className="border-0 overflow-hidden" style={{ borderRadius: "16px", boxShadow: "0 10px 40px rgba(0,0,0,0.06)", background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%)", border: "1px solid #bae6fd" }}>
                <CardBody>
                  <h6 className="fw-bold mb-2 d-flex align-items-center gap-2" style={{ color: "#0c4a6e" }}>
                    <span className="d-inline-flex align-items-center justify-content-center rounded-3" style={{ width: "36px", height: "36px", background: "rgba(14,165,233,0.2)" }}>
                      <i className="mdi mdi-information-outline" style={{ fontSize: "1.1rem" }} />
                    </span>
                    Editable Fields
                  </h6>
                  <p className="small mb-0" style={{ color: "#0369a1", lineHeight: 1.6 }}>
                    You can update your full name, email, mobile number, city, state, address, profile picture, and password. When changing password, confirm it matches.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <KycRequiredModal
        isOpen={showKycRequiredModal}
        toggle={handleCloseKycRequiredModal}
        variant={kycModalVariant}
        onCompleteKyc={kycSubmitted ? undefined : openKycCaptureFlow}
      />

      {/* Camera / KYC capture — opens after user confirms in KycRequiredModal */}
      <Modal isOpen={showKycCaptureModal} toggle={handleCloseKycCaptureModal} centered style={{ borderRadius: "16px", overflow: "hidden" }}>
        <ModalHeader toggle={handleCloseKycCaptureModal} className="border-0 pb-0" style={{ background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)" }}>
          <span className="d-flex align-items-center gap-2 fw-bold" style={{ color: "#92400e" }}>
            <i className="mdi mdi-shield-alert" style={{ fontSize: "1.5rem" }} />
            Complete your KYC
          </span>
        </ModalHeader>
        <ModalBody className="pt-3">
          {(() => {
            const profileComplete = userData?.profileComplete ?? !!(userData?.address?.trim() && userData?.pinCode?.trim() && userData?.mobileNumber?.trim() && userData?.aadharNumber?.trim())
            if (!profileComplete) {
              return (
                <Alert color="warning" className="mb-0">
                  Complete your profile first: add <strong>Address</strong>, <strong>Pin code</strong>, <strong>Mobile number</strong> and <strong>Aadhar number</strong> in Edit Profile. Then you can complete KYC.
                </Alert>
              )
            }
            return (
              <>
                <p className="mb-3" style={{ color: "#374151" }}>
                  Complete verification with a live selfie, a short video, and your signature upload.
                </p>
                {showKycCaptureModal && userData?._id && (
                  <Suspense fallback={<p className="mb-0 text-muted">Loading verification...</p>}>
                    <KycCapture
                      userId={String(userData._id)}
                      onSuccess={() => {
                        setShowKycCaptureModal(false)
                        setShowKycRequiredModal(false)
                        userDismissedKycRequiredAutoRef.current = true
                        loadProfile()
                      }}
                      onCancel={handleCloseKycCaptureModal}
                    />
                  </Suspense>
                )}
                {showKycCaptureModal && !userData?._id && (
                  <Alert color="warning" className="mb-0">Unable to load user. Please refresh the page.</Alert>
                )}
              </>
            )
          })()}
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default withRouter(UserProfile)
